/**
 * Pick colleagues by NAME instead of typing their address.
 *
 * Every interviewer field on this board was a comma-separated text box, so
 * setting up a panel meant typing out `firstname.lastname@finn.com` for each
 * person — slow, and a typo silently produces a panel member who does not exist
 * (Theresa, Sep 2026). A wrong address here is not cosmetic: the calendar read
 * for that person returns nothing, so the slot grader treats them as free and
 * the board offers times nobody can make.
 *
 * The directory this searches is the Google Workspace one, already used by the
 * Interview Links page (`services/directoryApi.ts` → `PersonPicker`). No new
 * permission, no new endpoint — it was simply never wired into the board.
 *
 * A typed address is still accepted. Somebody can be on a loop before the
 * directory knows them (a new joiner, a contractor), and refusing would block a
 * real panel — the same trade `PersonPicker`'s `allowTypedEmail` already makes
 * for interview links.
 */
import { Input } from '@finn-auto/design-system/atoms/input';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { cn } from '../../../../shared';
import { X } from 'lucide-react';
import { PersonPicker } from '../interviewLinks/PersonPicker';
import { fullNameFromEmail } from './format';

const isFinn = (e: string) => /^[^\s@]+@finn\.com$/.test(e.trim().toLowerCase());

/** Split a comma-separated field into addresses, keeping the caller's order. */
export function parseEmailList(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
    ),
  ];
}

/**
 * SEVERAL people — a panel pool, an audience.
 *
 * The value stays a comma-separated STRING rather than an array: every caller
 * already stores it that way, and keeping the raw text means a half-typed
 * address does not vanish on each keystroke. The picker appends to it.
 */
export function PeopleField({
  id,
  label,
  value,
  onChange,
  hint,
  pinned,
  onTogglePin,
  pinLabel = 'fixed',
  pinTitle,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  hint?: string;
  /**
   * Per-person flag rendered on the chip. Both props together or neither —
   * without a handler the chips stay plain, which is every other caller.
   */
  pinned?: string[];
  onTogglePin?: (email: string) => void;
  pinLabel?: string;
  pinTitle?: string;
}) {
  const emails = parseEmailList(value);
  const invalid = emails.filter((e) => !isFinn(e));

  const add = (email: string) => {
    if (emails.includes(email.toLowerCase())) return;
    onChange([...emails, email.toLowerCase()].join(', '));
  };
  const remove = (email: string) => onChange(emails.filter((e) => e !== email).join(', '));

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      <PersonPicker onSelect={(p) => add(p.email)} placeholder="Search by name…" />
      {/* The chips are the real state, and each is removable — reading a name
          off a comma-separated string to check a panel is exactly the friction
          this replaces. */}
      {emails.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {emails.map((e) => (
            <span
              key={e}
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs',
                pinned?.includes(e) ? 'bg-[#DCE9FA] text-[#0B4A87]' : 'bg-gray-100 text-gray-700'
              )}
            >
              {isFinn(e) ? fullNameFromEmail(e) : e}
              {/* The pin sits ON the name it applies to — a separate list of
                  "fixed people" would be a second place to keep in sync. */}
              {onTogglePin && (
                /*
                 * A real CHECKBOX, not the label acting as a button.
                 *
                 * It used to render the words "fixed panelist" on every chip —
                 * grey when off, blue with a 📌 when on — which reads as a
                 * STATEMENT about that person rather than a control. Every
                 * interviewer therefore looked already fixed, and nobody could
                 * see what to click to change it (Theresa, Sep 2026). A
                 * checkbox has one unambiguous off state and needs no colour to
                 * be understood.
                 */
                <label
                  className="inline-flex cursor-pointer items-center gap-1 font-medium"
                  title={pinTitle}
                >
                  <Checkbox
                    checked={pinned?.includes(e) ?? false}
                    onCheckedChange={() => onTogglePin(e)}
                    aria-label={`${pinLabel} — ${isFinn(e) ? fullNameFromEmail(e) : e}`}
                  />
                  <span className={pinned?.includes(e) ? 'text-[#0B4A87]' : 'text-gray-500'}>
                    {pinLabel}
                  </span>
                </label>
              )}
              {/*
                A bare button with the lucide X, matching InterviewerEditor's
                chip on the Interview Links page — the component this field
                mirrors, and the established shape for chip removal here. The
                design-system Button is a real button with its own height and
                padding; dropped inside a chip it dwarfs the label it removes.
              */}
              <button
                type="button"
                aria-label={`Remove ${e}`}
                className="text-gray-400 hover:text-gray-700"
                onClick={() => remove(e)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      {/* The text box stays, as the escape hatch AND the audit trail: pasting a
          list of five addresses is still the fastest way to set one of these up. */}
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange((e.target as HTMLInputElement).value)}
        placeholder="or paste addresses, comma-separated"
      />
      {invalid.length > 0 && (
        <p className="text-xs text-red-600">
          Not a @finn.com address: {invalid.join(', ')} — an interviewer must be staff, and a typo
          reads as “free all week” to the slot grader.
        </p>
      )}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

/** ONE person — the final interviewer, the bar raiser, a stand-in. */
export function PersonField({
  id,
  label,
  value,
  onChange,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  hint?: string;
}) {
  const clean = value.trim().toLowerCase();
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      {/* Chosen name above the box, so what is SET is legible at a glance
          without reading an address. */}
      {clean && isFinn(clean) && (
        <p className="text-xs text-gray-600">
          <span className="font-semibold">{fullNameFromEmail(clean)}</span> · {clean}
        </p>
      )}
      <PersonPicker onSelect={(p) => onChange(p.email)} placeholder="Search by name…" />
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange((e.target as HTMLInputElement).value)}
        placeholder="or type the @finn.com address"
      />
      {clean && !isFinn(clean) && (
        <p className="text-xs text-red-600">Not a @finn.com address.</p>
      )}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
}
